import { io, Socket } from 'socket.io-client';

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second

  // Connection state
  private isConnected = false;
  private isConnecting = false;

  // Event listeners
  private notificationListeners: ((data: any) => void)[] = [];

  constructor() {
    // Bind methods to maintain 'this' context
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.joinUserRoom = this.joinUserRoom.bind(this);
    this.handleConnect = this.handleConnect.bind(this);
    this.handleDisconnect = this.handleDisconnect.bind(this);
    this.handleNotification = this.handleNotification.bind(this);
    this.handleConnectError = this.handleConnectError.bind(this);
    this.handleReconnectAttempt = this.handleReconnectAttempt.bind(this);
  }

  /**
   * Connect to Socket.IO server
   * @param userId - Auth0 user ID for room joining
   * @param token - Auth0 JWT token for authentication
   */
  connect(userId: string, token: string): void {
    if (this.isConnecting || this.isConnected) {
      return;
    }

    this.isConnecting = true;

    // IMPORTANT: Use REACT_APP_BACKEND_URI (same as other API calls)
    const backendUrl = process.env.REACT_APP_BACKEND_URI || 'http://localhost:5000';

    this.socket = io(backendUrl, {
      auth: {
        token: token,
      },
      transports: ['polling'], // Force polling - reliable through load balancers
      timeout: 20000,
      forceNew: true,
    });

    // Set up event listeners
    this.socket.on('connect', () => this.handleConnect(userId));
    this.socket.on('disconnect', this.handleDisconnect);
    this.socket.on('connect_error', this.handleConnectError);
    this.socket.on('reconnect_attempt', this.handleReconnectAttempt);
    this.socket.on('new-notification', this.handleNotification);
  }

  /**
   * Disconnect from Socket.IO server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Join user-specific room for notifications
   * @param userId - Auth0 user ID
   */
  joinUserRoom(userId: string): void {
    if (this.socket && this.isConnected && userId) {
      // Ensure userId starts with 'auth0|' - this is critical for room matching
      this.socket.emit('join-user-room', userId);
    }
  }

  /**
   * Add notification event listener
   * @param callback - Function to call when notification received
   */
  addNotificationListener(callback: (data: any) => void): void {
    this.notificationListeners.push(callback);
  }

  /**
   * Remove notification event listener
   * @param callback - Function to remove
   */
  removeNotificationListener(callback: (data: any) => void): void {
    this.notificationListeners = this.notificationListeners.filter(
      listener => listener !== callback
    );
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): { isConnected: boolean; isConnecting: boolean } {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
    };
  }

  // Private event handlers

  private handleConnect(userId: string): void {
    this.isConnected = true;
    this.isConnecting = false;
    this.reconnectAttempts = 0;

    // Auto-join user room
    this.joinUserRoom(userId);
  }

  private handleDisconnect(reason: string): void {
    this.isConnected = false;
    this.isConnecting = false;

    // Attempt to reconnect if not manually disconnected
    if (reason !== 'io client disconnect') {
      this.attemptReconnect();
    }
  }

  private handleConnectError(error: Error): void {
    this.isConnecting = false;
    this.attemptReconnect();
  }

  private handleReconnectAttempt(attemptNumber: number): void {
    this.reconnectAttempts = attemptNumber;
  }

  private handleNotification(data: any): void {
    // Notify all listeners
    this.notificationListeners.forEach((callback, index) => {
      try {
        callback(data);
      } catch (error) {
        // console.error(`🔔 [SocketService] ❌ Error in listener #${index + 1}:`, error);
      }
    });
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000); // Exponential backoff, max 30s

    setTimeout(() => {
      if (!this.isConnected && this.socket) {
        this.socket.connect();
      }
    }, delay);
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
export { SocketService };
